import { useState, useRef, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'
import { useNotifications } from '../../hooks/useNotifications'
import { formatTimeAgo } from '../../utils/formatters'

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  const { preview: notifications, unreadCount, markAsRead, markAllAsRead, fetchPreview } = useNotifications()

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleOpen = () => {
    setIsOpen((prev) => !prev)
    if (!isOpen) fetchPreview()
  }

  const handleNotificationClick = (notification) => {
    setIsOpen(false)
    if (!notification.read) markAsRead(notification.id)
  }

  const handleMarkAllAsRead = (e) => {
    e.stopPropagation()
    markAllAsRead()
  }

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return 'mdi:check-circle'
      case 'warning':
        return 'mdi:alert'
      case 'error':
        return 'mdi:alert-circle'
      case 'info':
        return 'mdi:information'
      default:
        return 'mdi:bell'
    }
  }

  const getNotificationColor = (type) => {
    switch (type) {
      case 'success':
        return 'text-success'
      case 'warning':
        return 'text-warning'
      case 'error':
        return 'text-danger'
      case 'info':
        return 'text-info'
      default:
        return 'text-primary'
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleOpen}
        className="relative p-2 sm:p-2.5 text-text-secondary hover:bg-bg-light hover:text-primary rounded-lg transition-all duration-150"
        aria-label="Notifications"
      >
        <Icon icon="mdi:bell" className="text-lg sm:text-xl" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 bg-danger text-white text-xs px-1 sm:px-1.5 py-0.5 rounded-full min-w-[18px] sm:min-w-[20px] text-center text-[10px] sm:text-xs">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Mobile Overlay */}
          <div
            className="fixed inset-0 bg-black/20 z-40 sm:hidden"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="fixed sm:absolute top-16 sm:top-full right-2 sm:right-0 left-2 sm:left-auto mt-0 sm:mt-2 bg-white rounded-lg shadow-xl border border-border-light w-[calc(100%-1rem)] sm:w-auto sm:min-w-[320px] sm:max-w-[400px] max-h-[calc(100vh-5rem)] sm:max-h-[500px] overflow-hidden flex flex-col z-50 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between p-3 sm:p-4 border-b border-border-light">
              <h3 className="text-sm sm:text-base font-semibold text-text-dark">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  className="text-xs text-primary hover:underline active:text-primary-dark"
                  onClick={handleMarkAllAsRead}
                >
                  Mark all as read
                </button>
              )}
            </div>

            {/* Notifications List */}
            <div className="overflow-y-auto flex-1">
              {notifications.length > 0 ? (
                <div className="divide-y divide-border-light">
                  {notifications.map((notification) => (
                    <Link
                      key={notification.id}
                      to={notification.link || '#'}
                      onClick={() => handleNotificationClick(notification)}
                      className={`flex items-start gap-2 sm:gap-3 p-3 sm:p-4 hover:bg-bg-light active:bg-bg-light transition-colors ${!notification.read ? 'bg-primary/5' : ''
                        }`}
                    >
                      <div
                        className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${getNotificationColor(notification.type)
                          }/20`}
                      >
                        <Icon
                          icon={getNotificationIcon(notification.type)}
                          className={`text-lg sm:text-xl ${getNotificationColor(notification.type)}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-text-dark mb-0.5 sm:mb-1 line-clamp-1">
                          {notification.title}
                        </p>
                        <p className="text-xs text-text-secondary line-clamp-2 sm:line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-text-secondary mt-1">
                          {formatTimeAgo(notification.createdAt)}
                        </p>
                      </div>
                      {!notification.read && (
                        <div className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-1.5 sm:mt-2"></div>
                      )}
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-center px-4">
                  <Icon icon="mdi:bell-off" className="text-3xl sm:text-4xl text-text-secondary mb-2 sm:mb-3" />
                  <p className="text-xs sm:text-sm text-text-secondary">No notifications</p>
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="border-t border-border-light p-3 sm:p-4 text-center">
                <Link
                  to="/notifications"
                  onClick={() => setIsOpen(false)}
                  className="text-xs sm:text-sm text-primary hover:underline active:text-primary-dark font-medium inline-block py-1"
                >
                  View all notifications
                </Link>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default NotificationDropdown
